/* eslint-disable simple-import-sort/imports */
import { User as OidcUser } from 'oidc-client'

import { tokenAuth } from '../lib/axios'
import Message, {
  EditableMessage,
  getMessageFromType,
  NewType,
  PossibleType,
  Status,
  stringToLob,
  Type
} from '../models/Message'
import { splitMessageType } from '../utils/utils'
import { isValid } from 'date-fns'

interface MessageResponse {
  applicationId: string
  id: string
  type: string
  lob: string
  startDateTime: string
  endDateTime?: string
}

export const getDisruptionMessages = async (oidcUser: OidcUser): Promise<Array<Message>> => {
  const url = `${process.env.REACT_APP_BFF_BASE_URL}/disruption-messages`
  const authInstance = tokenAuth(oidcUser)

  const response = await authInstance.get<Array<MessageResponse>>(url)
  return response.data.map((message) => {
    const startDateTime = new Date(message.startDateTime)
    const endDateTime = isValid(new Date(message.endDateTime as string))
      ? new Date(message.endDateTime as string)
      : undefined
    const now = new Date()
    const isScheduled = startDateTime > now
    const isActive = !endDateTime || endDateTime > now
    const status = isScheduled ? Status.SCHEDULED : isActive ? Status.ACTIVE : Status.INACTIVE

    const { lineOfBusiness, type } = splitMessageType(message.type)
    return {
      applicationId: message.applicationId,
      id: message.id,
      type: type,
      message: getMessageFromType(type),
      startDateTime,
      endDateTime,
      status,
      lob: stringToLob(lineOfBusiness)
    }
  })
}

interface CreateMessageParams {
  type: Type | NewType | PossibleType
  startDateTime: string
  endDateTime: string
}
interface EditMessageParams {
  alertId: string
  startDateTime: string
  endDateTime: string
}

export const createDisruptionMessage = async (oidcUser: OidcUser, params: CreateMessageParams): Promise<void> => {
  const url = `${process.env.REACT_APP_BFF_BASE_URL}/disruption-messages`
  const authInstance = tokenAuth(oidcUser)

  await authInstance.post(url, params)
}

export const editDisruptionMessage = async (oidcUser: OidcUser, body: EditMessageParams): Promise<void> => {
  const url = `${process.env.REACT_APP_BFF_BASE_URL}/disruption-messages`
  const authInstance = tokenAuth(oidcUser)
  await authInstance.patch(url, body)
}

export const getDisruptionMessage = async (oidcUser: OidcUser, alertId: string): Promise<EditableMessage> => {
  const url = `${process.env.REACT_APP_BFF_BASE_URL}/disruption-messages/${alertId}`
  const authInstance = tokenAuth(oidcUser)
  const response = await authInstance.get<MessageResponse>(url)
  const { id, type, startDateTime, endDateTime } = response.data
  return {
    id,
    type,
    startDateTime: new Date(startDateTime),
    endDateTime: endDateTime ? new Date(endDateTime) : undefined
  }
}

export const deleteAlert = async (oidcUser: OidcUser, alertId: string): Promise<void> => {
  const url = `${process.env.REACT_APP_BFF_BASE_URL}/disruption-messages/${alertId}`
  const authInstance = tokenAuth(oidcUser)
  await authInstance.delete<Promise<void>>(url)
}
